import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import LocalizedText from '@LocalizedText';
import { isMobileMode } from '@app/utils/utils';
import useIntlMessage from '@app/hooks/use-intl-message';
import './Slider.scss';

const Slider = ({
  defaultValue,
  onChange,
  onFinishChanging,
  onChangeValueByStep,
  snapToEdgeVisible,
  mobileView,
  minValue,
  maxValue,
  step,
  filter,
  minValueLocaleKey,
  maxValueLocaleKey,
  ariaLabelLocaleKey,
  disabled,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [value, setValue] = useState(defaultValue);
  const [params, setParams] = useState(null);
  const sliderContainerRef = useRef();
  const sliderTrackRef = useRef();
  const sliderWrapperRef = useRef();
  const trackProgressRef = useRef();
  const thumbRef = useRef();

  const [sliderAriaLabel] = useIntlMessage(ariaLabelLocaleKey);

  const adjustForStep = useCallback(
    value => {
      return Math.round(value / step) * step;
    },
    [step],
  );

  const percentageValue = useMemo(() => {
    const range = maxValue - minValue;
    const adjustedValue = value - minValue;

    return (adjustedValue / range) * 100;
  }, [value, maxValue, minValue]);

  const handleKeyDown = useCallback(
    e => {
      if (disabled) return;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          if (onChangeValueByStep) {
            onChangeValueByStep(value + step);
          }
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          if (onChangeValueByStep) {
            onChangeValueByStep(value - step);
          }
          break;
        default:
          return;
      }
    },
    [value, step, disabled, onChangeValueByStep],
  );

  const updateParams = useCallback(() => {
    setParams({
      sliderContainerStart: Math.round(sliderContainerRef.current.getBoundingClientRect().left),
      sliderContainerWidth: sliderContainerRef.current.offsetWidth,
    });
  }, []);

  useEffect(() => {
    updateParams();
  }, [snapToEdgeVisible, updateParams]);

  useEffect(() => {
    document.body.addEventListener(mobileView ? 'touchmove' : 'mousemove', thumbMove);
    window.addEventListener(mobileView ? 'touchend' : 'mouseup', stopDragging);
    window.addEventListener('resize', updateParams);
    setPosition();
    return () => {
      document.body.removeEventListener(mobileView ? 'touchmove' : 'mousemove', thumbMove);
      window.removeEventListener(mobileView ? 'touchend' : 'mouseup', stopDragging);
      window.removeEventListener('resize', updateParams);
    };
  });

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const startDragging = () => {
    setIsDragging(true);
    document.body.addEventListener(mobileView ? 'touchcancel' : 'click', captureClick, true);
  };

  const stopDragging = () => {
    setIsDragging(false);
    onFinishChanging?.(value);
  };

  const captureClick = e => {
    e.stopPropagation();
    document.body.removeEventListener(mobileView ? 'touchcancel' : 'click', captureClick, true);
  };

  const setPosition = () => {
    if (!sliderContainerRef.current) return;

    const sliderContainerWidth = sliderContainerRef.current.offsetWidth;

    thumbRef.current.style.transform = `translate(-50%) translate(${(percentageValue / 100) * sliderContainerWidth}px)`;
    trackProgressRef.current.style.transform = `scaleX(${percentageValue / 100})`;
  };

  const thumbMove = e => {
    let translate;
    if (!isDragging || !sliderContainerRef.current) return;

    const clientX = mobileView ? e.touches[0].clientX : e.clientX;
    if (clientX === undefined) return;

    const { sliderContainerWidth, sliderContainerStart } = params;

    if (clientX < sliderContainerStart) {
      translate = 0;
    } else if (clientX > sliderContainerWidth + sliderContainerStart) {
      translate = sliderContainerWidth;
    } else {
      translate = clientX - sliderContainerStart;
    }

    const newValue = (translate / sliderContainerWidth) * maxValue;
    const nextValue = adjustForStep(newValue < minValue ? minValue : newValue);

    setValue(nextValue);
    setPosition();
    onChange?.(nextValue);
  };

  const onChangeByStep = e => {
    e.stopPropagation();

    if (!onChangeValueByStep) return;

    if (e.currentTarget === sliderTrackRef.current) {
      onChangeValueByStep(value + step);
    } else if (e.currentTarget === trackProgressRef.current) {
      onChangeValueByStep(value - step);
    }
  };

  return (
    <div className={classnames('slider-wrapper', { disabled })} ref={sliderWrapperRef}>
      <div className="slider-bound">
        <LocalizedText id={minValueLocaleKey} values={{ value: filter ? filter(minValue) : minValue }} tagName="span" />
        <LocalizedText id={maxValueLocaleKey} values={{ value: filter ? filter(maxValue) : maxValue }} tagName="span" />
      </div>
      <div
        className="slider-container"
        ref={sliderContainerRef}
        role="slider"
        aria-label={sliderAriaLabel}
        aria-valuemin={minValue}
        aria-valuemax={maxValue}
        aria-valuenow={value}
        aria-valuetext={filter ? filter(value) : value.toString()}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        <div aria-hidden className="slider-track" onClick={onChangeByStep} ref={sliderTrackRef}>
          <div aria-hidden className="slider-track-progress" onClick={onChangeByStep} ref={trackProgressRef} />
        </div>
        <div className="slider-thumb" ref={thumbRef}>
          {mobileView ? (
            <div className="slider-thumb-indicator" onTouchStart={startDragging} onTouchEnd={stopDragging} />
          ) : (
            <div className="slider-thumb-indicator" onMouseDown={startDragging} onMouseUp={stopDragging} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({
  matrix: { ctx },
  customize: {
    customizeData: { snapToEdgeVisible },
  },
}) => ({ mobileView: isMobileMode(ctx), snapToEdgeVisible });

Slider.propTypes = {
  onChange: PropTypes.func,
  onChangeValueByStep: PropTypes.func,
  onFinishChanging: PropTypes.func,
  filter: PropTypes.func,
  defaultValue: PropTypes.number.isRequired,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  minValueLocaleKey: PropTypes.string.isRequired,
  maxValueLocaleKey: PropTypes.string.isRequired,
  ariaLabelLocaleKey: PropTypes.string.isRequired,
  mobileView: PropTypes.bool.isRequired,
  snapToEdgeVisible: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default connect(mapStateToProps)(Slider);
